import React from 'react'
import { Send,} from 'lucide-react';
import { formatearFechaCompleta } from '../../utils/utils'
import { Edit3, Clock, User, Info, Trash2, Plus } from 'lucide-react';

const ModuloHistorial = ({historial}) => {
  
  const getTimeAgo = (fechacambio) => {
    if (!fechacambio) return '';
    
    const now = new Date();
    const changeDate = new Date(fechacambio);
    const diffInSeconds = Math.floor((now - changeDate) / 1000);
    
    const intervals = [
      { label: 'año', seconds: 31536000 },
      { label: 'mes', seconds: 2592000 },
      { label: 'día', seconds: 86400 },
      { label: 'hora', seconds: 3600 },
      { label: 'minuto', seconds: 60 }
    ];
    
    for (const interval of intervals) {
      const count = Math.floor(diffInSeconds / interval.seconds);
      if (count > 0) {
        return `hace ${count} ${interval.label}${count !== 1 ? (interval.label === 'mes' ? 'es' : 's') : ''}`;
      }
    }
    
    return 'hace un momento';
  };

  // Función para obtener el icono según el tipo
  const getChangeIcon = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'INFORMACION':
        return <Info size={16} className="text-blue-600" />;
      case 'CAMBIO':
        return <Edit3 size={16} className="text-gray-600" />;
      case 'ELIMINACION':
        return <Trash2 size={16} className="text-red-600" />;
      case 'NUEVO': // NUEVO
        return <Plus size={16} className="text-green-600" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  // Función para obtener el color del borde según el tipo
  const getBorderColor = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'INFORMACION':
        return 'border-blue-400';
      case 'CAMBIO':
        return 'border-gray-400';
      case 'ELIMINACION':
        return 'border-red-400';
      case 'NUEVO': // NUEVO
        return 'border-green-400';
      default:
        return 'border-gray-400';
    }
  };

  // Función para obtener el color de fondo del badge según el tipo
  const getBadgeColor = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'INFORMACION':
        return 'bg-blue-100 text-blue-800';
      case 'CAMBIO':
        return 'bg-gray-100 text-gray-800';
      case 'ELIMINACION':
        return 'bg-red-100 text-red-800';
      case 'NUEVO': // NUEVO
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Función para formatear los cambios de manera legible
  const formatearCambio = (cambioant, cambionew) => {
    if (!cambioant && !cambionew) return null;
    
    return (
      <div className="mt-2 text-sm">
        {cambioant && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gray-500 font-medium">Anterior:</span>
            <span className="bg-red-50 text-red-700 px-2 py-1 rounded text-xs font-mono">
              {cambioant}
            </span>
          </div>
        )}
        {cambionew && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500 font-medium">Nuevo:</span>
            <span className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs font-mono">
              {cambionew}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Función para obtener el texto del tipo en español
  const getTipoTexto = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'INFORMACION':
        return 'Información';
      case 'CAMBIO':
        return 'Cambio';
      case 'ELIMINACION':
        return 'Eliminación';
      case 'NUEVO': // NUEVO
        return 'Nuevo Registro';
      default:
        return 'Registro';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Historial de Cambios</h3>
        <div className="text-sm text-gray-500">
          {historial?.length || 0} registro(s)
        </div>
      </div>
      
      {historial && historial.length > 0 ? (
        <div className="space-y-4">
          {/* Timeline */}
          <div className="relative">
            {/* Línea vertical del timeline */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            
            {historial
              .sort((a, b) => new Date(b.fechacambio) - new Date(a.fechacambio)) // Ordenar por fecha descendente
              .map((item, index) => (
                <div key={index} className="relative flex items-start space-x-4 pb-6">
                  {/* Icono del timeline */}
                  <div className={`flex-shrink-0 w-16 h-16 rounded-full border-4 ${getBorderColor(item.tipo)} bg-white flex items-center justify-center shadow-sm`}>
                    {getChangeIcon(item.tipo)}
                  </div>
                  
                  {/* Contenido del cambio */}
                  <div className="flex-grow min-w-0">
                    <div className={`bg-white rounded-lg border-l-4 ${getBorderColor(item.tipo)} shadow-sm p-4`}>
                      {/* Header con tipo y fecha */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            {getTipoTexto(item.tipo)}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatearFechaCompleta(item.fechacambio)}
                          </p>
                        </div>
                        
                        {/* Badge con tipo */}
                        {item.tipo && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getBadgeColor(item.tipo)}`}>
                            {getTipoTexto(item.tipo)}
                          </span>
                        )}
                      </div>

                      {/* Motivo del cambio */}
                      {item.motivocambio && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Motivo:</span> {item.motivocambio}
                          </p>
                        </div>
                      )}

                      {/* Cambios antes/después - Solo para tipo CAMBIO */}
                      {item.tipo?.toUpperCase() === 'CAMBIO' && formatearCambio(item.cambioant, item.cambionew)}

                      {/* Información adicional - Solo para tipo INFORMACION */}
                      {item.tipo?.toUpperCase() === 'INFORMACION' && (item.cambionew || item.cambioant) && (
                        <div className="mt-2 text-sm">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-blue-800">
                              {item.cambionew || item.cambioant}
                            </p>
                          </div>
                        </div>
                      )}

                      {item.tipo?.toUpperCase() === 'ELIMINACION' && item.cambionew && (
                        <div className="mt-2 text-sm">
                          <div className="text-gray-500 font-medium mb-1">Dato Eliminado:</div>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-red-800 text-xs font-mono">
                              {/* Si 'cambionew' es un objeto (como el 'pagoEliminado'), 
                                JSON.stringify lo mostrará formateado.
                                Si es solo un string, lo mostrará directamente.
                              */}
                              {typeof item.cambionew === 'object' && item.cambionew !== null
                                ? <pre className="whitespace-pre-wrap">{JSON.stringify(item.cambionew, null, 2)}</pre>
                                : String(item.cambionew)
                              }
                            </p>
                          </div>
                        </div>
                      )}

                      {item.tipo?.toUpperCase() === 'NUEVO' && item.cambionew && (
                        <div className="mt-2 text-sm">
                          <div className="text-gray-500 font-medium mb-1">Dato Agregado:</div>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-green-800 text-xs font-mono">
                              {/* Si 'cambionew' es un objeto, lo formatea */}
                              {typeof item.cambionew === 'object' && item.cambionew !== null
                                ? <pre className="whitespace-pre-wrap">{JSON.stringify(item.cambionew, null, 2)}</pre>
                                : String(item.cambionew)
                              }
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Footer con usuario */}
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                          <User size={14} className="text-gray-400" />
                          <span className="text-xs text-gray-600">
                            Realizado por: <span className="font-medium">{item.cambiadoPor || 'Sistema'}</span>
                          </span>
                        </div>
                        
                        {/* Tiempo relativo */}
                        <span className="text-xs text-gray-400">
                          {getTimeAgo(item.fechacambio)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
            <Clock size={24} className="text-gray-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            No hay historial disponible
          </h3>
          <p className="text-sm text-gray-500">
            Los cambios realizados en esta solicitud aparecerán aquí
          </p>
        </div>
      )}

      {/* Resumen estadístico */}
      {historial && historial.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Resumen de Actividad</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Total de registros */}
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {historial.length}
              </div>
              <div className="text-xs text-gray-600">Total registros</div>
            </div>
            
            {/* Último registro */}
            <div className="text-center">
              <div className="text-sm font-medium text-gray-900">
                {getTimeAgo(historial
                  .sort((a, b) => new Date(b.fechacambio) - new Date(a.fechacambio))[0]?.fechacambio)}
              </div>
              <div className="text-xs text-gray-600">Último registro</div>
            </div>
            
            {/* Usuarios únicos */}
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {new Set(historial.map(h => h.cambioadoPor).filter(Boolean)).size}
              </div>
              <div className="text-xs text-gray-600">Usuarios</div>
            </div>
            
            {/* Información vs Cambios */}
            <div className="text-center">
              <div className="flex justify-center space-x-2 text-sm">
                <div className="text-blue-600 font-medium">
                  {historial.filter(h => h.tipo?.toUpperCase() === 'INFORMACION').length} Info
                </div>
                <div className="text-gray-400">|</div>
                <div className="text-gray-600 font-medium">
                  {historial.filter(h => h.tipo?.toUpperCase() === 'CAMBIO').length} Cambios
                </div>
              </div>
              <div className="text-xs text-gray-600">Distribución</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModuloHistorial
